// The assistant, docked beside the Workflows catalog.
//
// This is NOT a second chat. It renders the same conversation as the Home tab
// (AdminChatContext), so anything said here is already there, which is why
// "expand" is just a navigation, with no state to hand over.
//
// Deliberately unbranded and chrome-light: no product name, no strapline. It
// should read as the same assistant the owner already talks to on Home, moved
// next to the thing they're working on.

import { useEffect, useRef, useState } from "react";
import { ArrowUp, Expand, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useAdminChat } from "@/contexts/AdminChatContext";
import { useCopilotPlans, type CommitPlan } from "@/hooks/useCopilotPlans";
import { COPILOT_EXAMPLES } from "@/lib/workflows/copilot";
import PlanTurn from "./PlanTurn";

interface CopilotPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Continue this same conversation full-size on the Home tab. */
  onExpand: () => void;
  /** Called with the new draft's id once a plan is built. */
  onWorkflowCreated: (workflowId: string) => void;
  /** Overrides the live commit. Dev/preview use only. */
  commitPlan?: CommitPlan;
}

export default function CopilotPanel({
  open,
  onOpenChange,
  onExpand,
  onWorkflowCreated,
  commitPlan,
}: CopilotPanelProps) {
  const { messages, isThinking, followUps, send } = useAdminChat();
  const { pendingPlanId, committingId, buildPlan } = useCopilotPlans({
    onWorkflowCreated,
    commitPlan,
  });
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  // Keep the newest turn in view. The rAF lets the new message (and its plan
  // card) lay out first, so scrollHeight is final.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    });
    return () => cancelAnimationFrame(frame);
  }, [messages, isThinking]);

  const submit = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isThinking) return;
    setInput("");
    // The owner opened this from "Create workflow", so intent is established
    // by the entry point rather than guessed from phrasing.
    void send(trimmed, { workflowContext: true });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[34rem]"
        data-testid="workflow-copilot-panel"
      >
        {/* Screen-reader only: the visible header is deliberately nameless. */}
        <SheetTitle className="sr-only">Dibs assistant</SheetTitle>
        <SheetDescription className="sr-only">
          Ask for a workflow in plain English. The same conversation continues on the Home tab.
        </SheetDescription>

        {/* ── Header: just the controls (SheetContent supplies the close X) ── */}
        <div className="flex h-11 items-center border-b border-border pl-4 pr-12">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="-ml-2 h-7 gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={onExpand}
            data-testid="copilot-expand"
          >
            <Expand className="h-3.5 w-3.5" />
            Expand
          </Button>
        </div>

        {/* ── Transcript ── */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5">
          {messages.length === 0 ? (
            <div className="flex h-full flex-col justify-center">
              <p className="text-sm font-medium text-foreground">What should this workflow do?</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                Plain English is fine. If Dibs can't do part of it, I'll say so and offer the
                closest thing that works.
              </p>
              <div className="mt-4 flex flex-col gap-1.5">
                {COPILOT_EXAMPLES.map((example) => (
                  <button
                    key={example}
                    type="button"
                    onClick={() => submit(example)}
                    className="rounded-lg border border-border bg-card px-3 py-2 text-left text-xs text-foreground transition-colors hover:border-primary/40 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              {messages.map((message) =>
                message.role === "user" ? (
                  <div key={message.id} className="flex justify-end">
                    <p className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-primary px-3.5 py-2 text-sm leading-relaxed text-primary-foreground">
                      {message.content}
                    </p>
                  </div>
                ) : (
                  <div key={message.id} className="flex flex-col gap-3">
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                      {message.content}
                    </p>

                    <PlanTurn
                      message={message}
                      pendingPlanId={pendingPlanId}
                      committingId={committingId}
                      onBuild={buildPlan}
                      onRevise={() => composerRef.current?.focus()}
                      onOpenWorkflow={onWorkflowCreated}
                      onUseSuggestion={submit}
                    />
                  </div>
                ),
              )}

              {isThinking && (
                <p
                  className="flex items-center gap-2 text-sm text-muted-foreground"
                  data-testid="copilot-thinking"
                >
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Thinking…
                </p>
              )}

              {!isThinking && followUps.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  {followUps.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => submit(suggestion)}
                      className="rounded-lg border border-border bg-card px-3 py-2 text-left text-xs text-foreground transition-colors hover:border-primary/40 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Composer ── */}
        <div className="border-t border-border px-5 py-4">
          <div className="flex items-end gap-2 rounded-xl border border-border bg-background p-2 focus-within:border-primary/40">
            <Textarea
              ref={composerRef}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  submit(input);
                }
              }}
              placeholder="Ask anything..."
              rows={1}
              disabled={isThinking}
              data-testid="copilot-composer"
              className="max-h-32 min-h-[2.25rem] resize-none border-0 bg-transparent px-1.5 py-1.5 text-sm shadow-none focus-visible:ring-0"
            />
            <Button
              type="button"
              size="icon"
              className="h-8 w-8 shrink-0"
              disabled={isThinking || !input.trim()}
              onClick={() => submit(input)}
              aria-label="Send message"
            >
              {isThinking ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowUp className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
